/**
 * React hooks for the ju credit system.
 *
 * Hooks are factory-bound so each app injects its own Supabase client +
 * auth-detection hook. This keeps the package free of any specific app's
 * AuthContext. Usage:
 *
 *   // In jubit-ai-universe / jubuddy-game shared bootstrap:
 *   export const { useCreditBalance, useImageUnlock, invalidateCreditBalanceCache } =
 *     createCreditsHooks({ supabase, useAuthState });
 *
 *   // Then in components:
 *   const { balance, isLoading, refresh } = useCreditBalance();
 *
 * The cache is module-scoped (a Map keyed by user id), so it's shared
 * across every consumer of the same hook factory instance — exactly
 * matches the previous behaviour in src/hooks/credits/useCreditBalance.ts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_BALANCE, SPEND_COST } from './constants.js';
import { createCreditsService } from './service.js';
import { canAccessModel } from './tier.js';
const CACHE_TTL_MS = 30 * 1000;
function costForTier(tier) {
    if (tier === 'pro')
        return SPEND_COST.IMAGE_UNLOCK_PRO;
    if (tier === 'max')
        return SPEND_COST.IMAGE_UNLOCK_MAX;
    return 0;
}
/**
 * Factory: bind the hooks to a specific Supabase client + auth source.
 * Returns the React-hook trio plus a global cache invalidator.
 */
export function createCreditsHooks({ supabase, useAuthState, }) {
    const service = createCreditsService(supabase);
    const cache = new Map();
    function useCreditBalance() {
        const { user } = useAuthState();
        const isAuthed = Boolean(user?.id);
        const key = user?.id ?? 'anon';
        const [balance, setBalance] = useState(EMPTY_BALANCE);
        const [isLoading, setIsLoading] = useState(false);
        const abortRef = useRef(null);
        const fetchBalance = useCallback(async () => {
            if (!isAuthed) {
                setBalance(EMPTY_BALANCE);
                return;
            }
            const cached = cache.get(key);
            if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
                setBalance(cached.data);
                return;
            }
            if (abortRef.current)
                abortRef.current.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setIsLoading(true);
            try {
                const next = await service.getUserBalance();
                if (controller.signal.aborted)
                    return;
                cache.set(key, { data: next, fetchedAt: Date.now() });
                setBalance(next);
            }
            finally {
                if (!controller.signal.aborted)
                    setIsLoading(false);
            }
        }, [isAuthed, key]);
        useEffect(() => {
            void fetchBalance();
            return () => {
                if (abortRef.current)
                    abortRef.current.abort();
            };
        }, [fetchBalance]);
        const refresh = useCallback(() => {
            cache.delete(key);
            void fetchBalance();
        }, [key, fetchBalance]);
        return { balance, isLoading, refresh };
    }
    function useImageUnlock({ userTier, modelA, modelB }) {
        const { balance, refresh } = useCreditBalance();
        // Build locked list lazily; identity is stable across renders when the
        // inputs don't change so executeSpend's useCallback can memoize.
        const locked = (() => {
            const out = [];
            if (modelA && !canAccessModel(modelA, userTier))
                out.push(modelA);
            if (modelB && !canAccessModel(modelB, userTier))
                out.push(modelB);
            return out;
        })();
        const totalCost = locked.reduce((sum, m) => sum + costForTier(m.tier), 0);
        const canAfford = totalCost === 0 || balance.creditsAvailable >= totalCost;
        const executeSpend = useCallback(async () => {
            if (totalCost === 0)
                return true;
            // One spend call per locked model so the audit log captures which
            // model was unlocked. Sequential to avoid racing.
            for (const model of locked) {
                const cost = costForTier(model.tier);
                if (cost === 0)
                    continue;
                const result = await service.spendCredits('unlock', cost, {
                    contentType: 'image_model',
                    metadata: { modelId: model.id, tier: model.tier },
                });
                if (result.status !== 'ok') {
                    cache.clear();
                    refresh();
                    return false;
                }
            }
            cache.clear();
            refresh();
            return true;
        }, [locked, totalCost, refresh]);
        return { lockedModels: locked, totalCost, canAfford, executeSpend };
    }
    function invalidateCreditBalanceCache() {
        cache.clear();
    }
    return { useCreditBalance, useImageUnlock, invalidateCreditBalanceCache };
}
